import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { queryClient } from '~/index';
import { AuthProtocolLoginResponse } from '~/swagger/data-contracts';
import { HttpClient } from '~/swagger/http-client';
import { clearAuth, refreshAuth } from './auth';

export const BaseApiUrl = process.env.REACT_APP_API_ROOT;

const axiosConfig = {
  baseURL: BaseApiUrl,
  headers: {},
};

export const http: HttpClient = new HttpClient(axiosConfig);

class BaseApiClass {
  constructor() {
    (function initializeToErrorHandling() {
      const requestWaitQueue: ((token: string) => void)[] = [];
      let isInProgressRefreshToken = false;

      function report(message: string, unknown?: boolean) {
        // TODO
      }

      async function updateToken() {
        isInProgressRefreshToken = true;

        const data = queryClient.getQueryData<AuthProtocolLoginResponse>(['auth']);
        const refreshToken = data?.refreshToken;

        try {
          if (refreshToken) {
            return await refreshAuth(refreshToken);
          } else {
            throw new Error('refresh token is not exist.');
          }
        } catch (e) {
          clearAuth();
        } finally {
          isInProgressRefreshToken = false;
        }
      }

      const responseInterceptor = async (error?: any) => {
        if (!error || !error.response) {
          report(error.message);
          return;
        }

        if (error.config?.url.indexOf('/auth/refresh') >= 0) {
          clearAuth();
          return;
        }

        const status = error.response.status;
        const message = error.response.data ? error.response.data.error : 'An error occurred, Please try again later.';

        if (error.config && status === 401) {
          const { config: requestConfigToRetry } = error;

          if (!isInProgressRefreshToken) {
            updateToken().then((accessToken) => {
              while (accessToken && requestWaitQueue.length > 0) {
                const waitingRequest = requestWaitQueue.shift();

                if (waitingRequest) {
                  waitingRequest(accessToken);
                }
              }
            });
          }

          return new Promise((resolve) => {
            requestWaitQueue.push((newAccessToken) => {
              requestConfigToRetry['headers'] = {
                ...requestConfigToRetry.headers,
                Authorization: `Bearer ${newAccessToken}`,
              };

              resolve(axios.request(requestConfigToRetry));
            });
          });
        } else if (status >= 500 || status === 422) {
          // Major Error
          report(message, true);
        } else {
          report(message);
        }

        return Promise.reject(error);
      };

      axios.interceptors.response.use(undefined, responseInterceptor);

      http.instance.interceptors.response.use(undefined, responseInterceptor);
    })();
  }

  get<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    axiosConfig = {} as AxiosRequestConfig<D>,
    auth = true
  ): Promise<R> {
    const authData = queryClient.getQueryData<AuthProtocolLoginResponse>(['auth']);
    const accessToken = authData?.accessToken;

    if (auth && accessToken) {
      axiosConfig['headers'] = { Authorization: `Bearer ${accessToken}` };
    }

    return axios.get(BaseApiUrl + url, axiosConfig);
  }

  post<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    axiosConfig = {} as AxiosRequestConfig<D>,
    auth = true
  ): Promise<R> {
    const authData = queryClient.getQueryData<AuthProtocolLoginResponse>(['auth']);
    const accessToken = authData?.accessToken;

    if (auth && accessToken) {
      axiosConfig['headers'] = { Authorization: `Bearer ${accessToken}` };
    }

    return axios.post(BaseApiUrl + url, data, axiosConfig);
  }
}

http.instance.interceptors.request.use((config) => {
  const newConfig = {
    baseURL: BaseApiUrl,
    ...config,
  };

  const authData = queryClient.getQueryData<AuthProtocolLoginResponse>(['auth']);
  const accessToken = authData?.accessToken;

  if (accessToken) {
    newConfig.headers.Authorization = `Bearer ${accessToken}`;
  }

  return newConfig;
});

const BaseApi = new BaseApiClass();

export default BaseApi;
